import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "General Enquiry",
  ...SERVICES.map((s) => s.title),
];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3 text-sm text-cream placeholder:text-cream/30 outline-none transition-colors duration-300 focus:border-gold/60";

export default function ConsultModal({ open, onOpenChange, presetService }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "General Enquiry",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && presetService) {
      setForm((f) => ({ ...f, service: presetService }));
    }
  }, [open, presetService]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been received.", {
        description: "Our team will reach out within 24 hours.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "General Enquiry",
        message: "",
      });
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong.", {
        description: "Please try again or email us at office@mkplus.llc.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="consultation-modal"
        className="border-gold/25 bg-noir text-cream sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-cream">
            Book Your <span className="italic text-gold">Free Consultation</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-cream/50">
            Tell us about your business — we'll respond within 24 hours.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid="consult-name-input"
              required
              placeholder="Full name"
              value={form.name}
              onChange={set("name")}
              className={inputCls}
            />
            <input
              data-testid="consult-email-input"
              required
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid="consult-phone-input"
              placeholder="Phone (optional)"
              value={form.phone}
              onChange={set("phone")}
              className={inputCls}
            />
            <select
              data-testid="consult-service-select"
              value={form.service}
              onChange={set("service")}
              className={`${inputCls} appearance-none [&>option]:bg-noir`}
            >
              {SERVICE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="consult-message-input"
            required
            rows={4}
            placeholder="Tell us about your goals..."
            value={form.message}
            onChange={set("message")}
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="consult-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-gold w-full disabled:opacity-60"
          >
            {loading ? "Sending..." : "Request Free Consultation"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
